import {
  Autocomplete,
  Box,
  Checkbox,
  FormControlLabel,
  InputAdornment,
  TextField,
  ToggleButton,
  Typography,
  useTheme,
} from "@mui/material";
import { DesktopDatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";
import { format as formatDate, isValid, parseISO } from "date-fns";
import { useState } from "react";
import { FormField, NoteAttachmentView } from "../dtos";
import { formatThousands, isNumericEntry, stripThousands } from "../utils/format-number";
import {
  OTHER_OPTION,
  resolveOptions,
  useOptionSources,
} from "../hooks/use-option-sources.hook";
import { FieldShell, SelectChevron, fieldInputSx } from "./field-shell";
import PhoneField from "./phone-field";

/**
 * One dynamic form input, switched on the workflow-manager field type. Every field
 * follows the Figma pattern: 11px caption label above a 36px white box (FieldShell).
 * Radio/option groups render as detached filter chips ("New loan / Top up / Refinance"),
 * computed values render boxless. Unknown types fall back to a plain text field.
 */
/** Advance a date by N business days (Sat/Sun skipped), as an ISO yyyy-mm-dd for a date input. */
const addBusinessDays = (from: Date, days: number): string => {
  const date = new Date(from.getFullYear(), from.getMonth(), from.getDate());
  let added = 0;
  while (added < days) {
    date.setDate(date.getDate() + 1);
    const day = date.getDay();
    if (day !== 0 && day !== 6) added += 1;
  }
  return date.toISOString().slice(0, 10);
};

const FormFieldInput = ({
  field,
  value,
  onChange,
  disabled,
  hiddenOptions,
  parentValue,
  error,
  returnedReason,
  returnedAttachments,
}: {
  field: FormField;
  value: unknown;
  onChange: (value: unknown) => void;
  disabled: boolean;
  /** §Option-visibility: option values the server judged hidden for this field (optWhen) — filtered out
   * so an agent only sees the choices actually offered (e.g. the credit-feedback remedies Credit offered). */
  hiddenOptions?: string[];
  /** §OptionSources: current value of the field this one's source `dependsOn` (compound ← developer). */
  parentValue?: unknown;
  /** §Rules: live verdict of this field's template validate rule (see utils/validate.ts) */
  error?: string;
  /** §Field-review: the reviewer's send-back reason for THIS field (fieldReviews[key].comment). */
  returnedReason?: string | null;
  returnedAttachments?: NoteAttachmentView[];
}) => {
  const theme = useTheme();
  const colors = theme.nawy.colors.semantic;
  // §Calendar: the date field's popover is CONTROLLED so tapping anywhere on the box opens it —
  // the design opens the calendar from the input itself, not only from the trailing icon.
  const [calendarOpen, setCalendarOpen] = useState(false);
  const required = field.req === 1 || field.req === true;
  const label = required ? `${field.l} *` : field.l;
  const stringValue = value === undefined || value === null ? "" : String(value);
  const inputSx = fieldInputSx(theme);
  const hidden = hiddenOptions?.length ? new Set(hiddenOptions) : null;
  // §OptionSources: a select carries EITHER inline `opt` values or an `optionsFrom` source key the
  // tenant registry resolves. Before this the latter fell through to a disabled box reading "managed
  // by the workflow console", which made Compound Data unfillable from the CRM tab.
  const { data: registry, isLoading: registryLoading } = useOptionSources();
  const bound = resolveOptions(registry?.sources, field.optionsFrom, parentValue);
  /**
   * §Other: a registry-bound select ALWAYS offers "Other" — the escape hatch for a developer or compound
   * that is not in the tenant's config. It is what reveals the follow-up fields the template gates on it
   * ("Compound of Interest (other)"), so without it those fields were unreachable from the CRM tab: the
   * value could never be set, so the condition could never be met. Matches workflow-manager's own renderer
   * (see its OTHER_OPTION), including that "Other" belongs to no parent and so survives every dependent
   * narrowing — a developer with no registered compounds still offers it.
   *
   * Gated on `bound.ready` so a still-loading registry never presents "Other" as the only choice and
   * tempts someone into saving it.
   */
  const withOther =
    field.optionsFrom && bound.ready ? [...bound.options, OTHER_OPTION] : bound.options;
  const sourced = field.opt ?? (field.optionsFrom ? withOther : undefined);
  // hidden-option filtering runs AFTER the append, exactly as workflow-manager does it, so a template that
  // gates "Other" itself through optWhen can still take it away.
  const options = hidden ? sourced?.filter((o) => !hidden.has(o)) : sourced;

  if (
    (field.t === "radio" || field.t === "select") &&
    (options?.length || (field.t === "select" && field.optionsFrom && bound.ready))
  ) {
    if (field.t === "radio") {
      return (
        <FieldShell label={label} help={field.help} error={error} returnedReason={returnedReason} returnedAttachments={returnedAttachments}>
          <Box
            role="radiogroup"
            aria-label={field.l}
            sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}
          >
            {(options ?? []).map((option) => (
              // standalone ToggleButtons (no ToggleButtonGroup): the group's
              // first/middle/last classes collapse borders and radii
              <ToggleButton
                key={option}
                value={option}
                selected={stringValue === option}
                disabled={disabled}
                onChange={() => onChange(option)}
                sx={{
                  // §MobileQA: the design's chips are 36px tall, but the option labels are
                  // TEMPLATE data — a long engine-authored option has to grow the chip (the
                  // design wraps chips onto new rows) instead of being clipped by a fixed
                  // height. minHeight keeps the 36px resting size for ordinary options.
                  minHeight: 36,
                  maxWidth: "100%",
                  py: 0.5,
                  px: 2,
                  borderRadius: theme.nawy.radius.lg,
                  textTransform: "none",
                  fontSize: 14,
                  fontWeight: 400,
                  lineHeight: "20px",
                  color: colors.text.primary,
                  border: `1px solid ${colors.border.primary}`,
                  backgroundColor: colors.layer.primary,
                  "&.Mui-selected": {
                    backgroundColor: colors.layer.accentLight,
                    borderColor: colors.border.accent,
                    color: colors.text.accent,
                    "&:hover": { backgroundColor: colors.layer.accentLight },
                  },
                }}
              >
                {option}
              </ToggleButton>
            ))}
          </Box>
        </FieldShell>
      );
    }
    const list = options ?? [];
    // §Other: both hints ask whether the REGISTRY had anything, not whether the rendered list does — the
    // appended "Other" makes the rendered list never empty, which would silence them both.
    // a dependent source with no parent chosen yet is EMPTY on purpose — say so instead of
    // presenting an inexplicably empty dropdown.
    const waitingOnParent = Boolean(
      field.optionsFrom && bound.dependsOn && !bound.options.length
    );
    // an INDEPENDENT bound source with nothing in it means the tenant list is empty (or still
    // loading) — say which, so an un-fillable required select is never just mysteriously greyed out.
    const emptySource = Boolean(
      field.optionsFrom && !bound.dependsOn && !bound.options.length
    );
    return (
      <FieldShell
        label={label}
        returnedReason={returnedReason}
        returnedAttachments={returnedAttachments}
        help={
          waitingOnParent
            ? `Choose a ${bound.dependsOn?.replace(/s$/, "")} first`
            : emptySource
              ? registryLoading
                ? "Loading options…"
                : `No ${field.l.toLowerCase()} options are configured yet`
              : field.help
        }
      >
        {/* §BoundSelect: an Autocomplete rather than a plain select. These lists are tenant CONFIG —
            the developer list runs to dozens of entries and the compound list to hundreds — and a
            native-style menu makes you scroll for a name you already know. Typing filters it.

            Not `freeSolo`: the escape hatch is the "Other" OPTION, which is what the template's
            conditions key off. Letting someone type an arbitrary developer here would save a value no
            condition matches and no config recognises, which is the bug "Other" exists to avoid. */}
        <Autocomplete
          size="small"
          fullWidth
          options={list}
          value={list.includes(stringValue) ? stringValue : null}
          onChange={(_, next) => onChange(next ?? "")}
          disabled={disabled || !list.length}
          popupIcon={<SelectChevron />}
          // one canonical spelling per option, so no custom equality is needed
          isOptionEqualToValue={(option, selected) => option === selected}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder={waitingOnParent ? "" : "Select or type to search"}
              sx={inputSx}
              slotProps={{
                htmlInput: { ...params.inputProps, "aria-label": field.l },
              }}
            />
          )}
          sx={{
            // the chevron is a lucide glyph, not MUI's filled caret — its box needs no rotation origin
            // fix, but the open-state flip is MUI's and is kept.
            "& .MuiAutocomplete-popupIndicator": { mr: 0.25 },
          }}
        />
      </FieldShell>
    );
  }

  switch (field.t) {
    case "checkbox":
      return (
        <FormControlLabel
          control={
            <Checkbox
              checked={Boolean(value)}
              onChange={(event) => onChange(event.target.checked)}
              disabled={disabled}
            />
          }
          label={label}
          slotProps={{
            typography: {
              sx: {
                fontSize: 14,
                lineHeight: "20px",
                color: colors.text.primary,
              },
            },
          }}
        />
      );
    case "textarea":
      return (
        <FieldShell label={label} help={field.help} error={error} returnedReason={returnedReason} returnedAttachments={returnedAttachments}>
          <TextField
            size="small"
            fullWidth
            multiline
            minRows={3}
            value={stringValue}
            onChange={(event) => onChange(event.target.value)}
            disabled={disabled}
            sx={fieldInputSx(theme, { multiline: true })}
            slotProps={{ htmlInput: { "aria-label": field.l } }}
          />
        </FieldShell>
      );
    case "date": {
      // §BusinessDays: a date the flow requires to be N WORKING days out (the seller transfer date is
      // "3 business days from today" in the designs). Config-driven off the field, so the rule lives
      // in the template with the field it constrains — the picker just refuses anything earlier.
      const minDays = Number(field.minBusinessDays);
      const min = Number.isFinite(minDays) && minDays > 0
        ? addBusinessDays(new Date(), minDays)
        : undefined;
      return (
        <FieldShell
          label={label}
          returnedReason={returnedReason}
          returnedAttachments={returnedAttachments}
          help={
            min
              ? `${field.help ? `${field.help} · ` : ""}${minDays} business day${
                  minDays === 1 ? "" : "s"
                } from today at the earliest`
              : field.help
          }
        >
          {/* §Calendar (design "Booking — Transfer date"): an in-card calendar popover anchored
              under the field, NOT the OS date sheet a native `type="date"` input hands to a phone.
              DesktopDatePicker keeps the popper on touch too (the plain DatePicker would swap to
              MUI's full-screen mobile dialog). Stored value stays the same `yyyy-MM-dd` string the
              native input produced — only the presentation changes. */}
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DesktopDatePicker
              value={stringValue ? parseISO(stringValue) : null}
              onChange={(next) =>
                onChange(next && isValid(next) ? formatDate(next, "yyyy-MM-dd") : "")
              }
              minDate={min ? parseISO(min) : undefined}
              disabled={disabled}
              open={calendarOpen}
              onOpen={() => setCalendarOpen(true)}
              onClose={() => setCalendarOpen(false)}
              // design placeholder reads "DD / MM / YYYY"
              format="dd / MM / yyyy"
              slotProps={{
                textField: {
                  size: "small",
                  fullWidth: true,
                  sx: {
                    ...inputSx,
                    // design: the empty mask is Text/Tertiary, not MUI's faded ink
                    "& .MuiOutlinedInput-input::placeholder": {
                      color: colors.text.tertiary,
                      opacity: 1,
                    },
                    // fieldInputSx dresses END adornments as the money "EGP" suffix chip — the
                    // calendar toggle is an icon button, so keep it unboxed.
                    "& .MuiInputAdornment-positionEnd": {
                      backgroundColor: "transparent",
                      p: 0,
                      color: colors.text.secondary,
                    },
                  },
                  // Open from anywhere on the box. This handler MUST sit on the TextField root,
                  // not on slotProps.htmlInput: the picker installs its own handlers on the inner
                  // input, so an htmlInput onClick never fires and tapping the field did nothing —
                  // worse than the native input it replaced, which opened the OS picker on tap.
                  // Verified at 390px: field-tap opens 42 day cells; the icon button still works.
                  onClick: () => {
                    if (!disabled) setCalendarOpen(true);
                  },
                  slotProps: {
                    htmlInput: { "aria-label": field.l },
                  },
                },
                popper: { placement: "bottom-start" },
                // the theme defines no palette.primary, so an unstyled picker paints today's ring
                // and the selected day MUI blue (#1976d2) — pin both to the brand accent.
                day: {
                  sx: {
                    "&.MuiPickersDay-today": { borderColor: colors.border.accent },
                    "&.Mui-selected": {
                      backgroundColor: colors.layer.accent,
                      color: colors.text.onColor,
                      "&:hover, &:focus": { backgroundColor: colors.layer.accentDark },
                    },
                  },
                },
              }}
            />
          </LocalizationProvider>
        </FieldShell>
      );
    }
    case "money":
      // §Separators (WM parity): grouped digits on DISPLAY, raw digits in the stored value — a text
      // input, not a native number input, so there are no spinner arrows and no focused-wheel
      // decrement. Money is never negative: '-' (and exponent notation) is blocked at the keystroke.
      return (
        <FieldShell label={label} help={field.help} error={error} returnedReason={returnedReason} returnedAttachments={returnedAttachments}>
          <TextField
            size="small"
            fullWidth
            value={formatThousands(stringValue)}
            onChange={(event) => {
              const raw = stripThousands(event.target.value);
              if (isNumericEntry(raw)) onChange(raw);
            }}
            onKeyDown={(event) => {
              if (["-", "e", "E", "+"].includes(event.key)) event.preventDefault();
            }}
            disabled={disabled}
            error={Boolean(error)}
            sx={inputSx}
            slotProps={{
              input: {
                endAdornment: (
                  <InputAdornment position="end">EGP</InputAdornment>
                ),
              },
              htmlInput: { "aria-label": field.l, inputMode: "decimal" },
            }}
          />
        </FieldShell>
      );
    case "number":
      // §Separators: same grouped-display / raw-storage treatment as money (negatives allowed here).
      return (
        <FieldShell label={label} help={field.help} error={error} returnedReason={returnedReason} returnedAttachments={returnedAttachments}>
          <TextField
            size="small"
            fullWidth
            value={formatThousands(stringValue)}
            onChange={(event) => {
              const raw = stripThousands(event.target.value);
              if (isNumericEntry(raw, true)) onChange(raw);
            }}
            disabled={disabled}
            error={Boolean(error)}
            sx={inputSx}
            slotProps={{ htmlInput: { "aria-label": field.l, inputMode: "decimal" } }}
          />
        </FieldShell>
      );
    case "computed":
      // read-only derived value — boxless per the design ("Requested loan amount")
      return (
        <FieldShell label={field.l} returnedReason={returnedReason} returnedAttachments={returnedAttachments}>
          <Typography
            sx={{
              fontSize: 14,
              lineHeight: "20px",
              color: colors.text.primary,
            }}
          >
            {stringValue || "—"}
          </Typography>
        </FieldShell>
      );
    case "select":
      // a select that reached here names no inline options AND no resolvable source — nothing to
      // offer, so keep it read-only rather than render an empty dropdown.
      return (
        <FieldShell
          label={label}
          help="Options are managed by the workflow console"
          returnedReason={returnedReason}
          returnedAttachments={returnedAttachments}
        >
          <TextField
            size="small"
            fullWidth
            value={stringValue}
            disabled
            sx={inputSx}
            slotProps={{ htmlInput: { "aria-label": field.l } }}
          />
        </FieldShell>
      );
    case "tel":
      return (
        <FieldShell label={label} help={field.help} error={error} returnedReason={returnedReason} returnedAttachments={returnedAttachments}>
          <PhoneField
            value={stringValue}
            onChange={onChange}
            disabled={disabled}
            ariaLabel={field.l}
          />
        </FieldShell>
      );
    case "email":
    case "text":
    default:
      return (
        <FieldShell label={label} help={field.help} error={error} returnedReason={returnedReason} returnedAttachments={returnedAttachments}>
          <TextField
            size="small"
            fullWidth
            type={field.t === "email" ? "email" : "text"}
            value={stringValue}
            onChange={(event) => onChange(event.target.value)}
            disabled={disabled}
            error={Boolean(error)}
            sx={inputSx}
            slotProps={{ htmlInput: { "aria-label": field.l } }}
          />
        </FieldShell>
      );
  }
};

export default FormFieldInput;
